import { useEffect, useMemo, useState } from "react";
import {
  FiBox,
  FiDownload,
  FiEdit2,
  FiPlus,
  FiSearch,
  FiTrash2,
  FiUpload,
} from "react-icons/fi";
import BrandModal from "../../components/masters/BrandModal";
import { useAuth } from "../../context/AuthContext";
import { apiRequest } from "../../services/api";
import ClientPagination, { useClientPagination } from "../../components/common/ClientPagination";

const getImageUrl = (storageKey) => {
  if (!storageKey) return "";

  if (storageKey.startsWith("http://") || storageKey.startsWith("https://")) {
    return storageKey;
  }

  const baseUrl = (
    process.env.REACT_APP_ASSET_URL ||
    process.env.REACT_APP_API_URL ||
    ""
  ).replace(/\/$/, "");

  return `${baseUrl}${storageKey.startsWith("/") ? "" : "/"}${storageKey}`;
};

export default function BrandsPage() {
  const { accessToken } = useAuth();

  const [brands, setBrands] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [showModal, setShowModal] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");

  const loadBrands = async () => {
    if (!accessToken) return;

    try {
      setIsLoading(true);
      setError("");

      const response = await apiRequest("/brands", {
        accessToken,
      });

      setBrands(Array.isArray(response) ? response : response?.data || []);
    } catch (requestError) {
      setError(requestError.message || "Unable to load brands.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadBrands();
  }, [accessToken]);

  const filteredBrands = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return brands.filter((brand) => {
      const matchesSearch =
        !normalizedSearch ||
        brand.name.toLowerCase().includes(normalizedSearch) ||
        brand.code.toLowerCase().includes(normalizedSearch);

      const matchesStatus =
        statusFilter === "ALL" ||
        (statusFilter === "ACTIVE" && brand.isActive) ||
        (statusFilter === "INACTIVE" && !brand.isActive);

      return matchesSearch && matchesStatus;
    });
  }, [brands, search, statusFilter]);

  const activeBrands = brands.filter((brand) => brand.isActive).length;
  const brandsPagination = useClientPagination(filteredBrands);

  const productsLinked = brands.reduce(
    (total, brand) =>
      total + (brand._count?.products || brand.products?.length || 0),
    0,
  );

  const handleSave = async (payload) => {
    setActionError("");

    if (selectedBrand) {
      await apiRequest(`/brands/${selectedBrand.id}`, {
        method: "PATCH",
        body: payload,
        accessToken,
      });
    } else {
      await apiRequest("/brands", {
        method: "POST",
        body: payload,
        accessToken,
      });
    }

    await loadBrands();
    setSelectedBrand(null);
  };

  const handleDelete = async (brand) => {
    const confirmed = window.confirm(
      `Delete "${brand.name}"? This cannot be undone.`,
    );

    if (!confirmed) return;

    try {
      setActionError("");

      await apiRequest(`/brands/${brand.id}`, {
        method: "DELETE",
        accessToken,
      });

      await loadBrands();
    } catch (requestError) {
      setActionError(requestError.message || "Unable to delete brand.");
    }
  };

  const openCreateModal = () => {
    setSelectedBrand(null);
    setShowModal(true);
  };

  const openEditModal = (brand) => {
    setSelectedBrand(brand);
    setShowModal(true);
  };

  return (
    <div>
      <div className="mb-8 flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-sm text-gray-500">Masters / Brands</p>

          <h1 className="mt-1 text-3xl font-bold text-gray-900">
            Brands Management
          </h1>

          <p className="mt-2 text-gray-500">
            Manage brands, logos, and linked products in your ERP.
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            disabled
            title="Import will be added later"
            className="flex h-11 items-center gap-2 rounded-xl border border-gray-200 bg-white px-5 font-medium opacity-60"
          >
            <FiUpload />
            Import
          </button>

          <button
            type="button"
            disabled
            title="Export will be added later"
            className="flex h-11 items-center gap-2 rounded-xl border border-gray-200 bg-white px-5 font-medium opacity-60"
          >
            <FiDownload />
            Export
          </button>

          <button
            type="button"
            onClick={openCreateModal}
            className="flex h-11 items-center gap-2 rounded-xl bg-primary-700 px-5 font-medium text-white hover:bg-primary-800"
          >
            <FiPlus />
            Add Brand
          </button>
        </div>
      </div>

      <div className="mb-8 grid gap-5 md:grid-cols-4">
        <StatCard label="Total Brands" value={brands.length} />

        <StatCard
          label="Active Brands"
          value={activeBrands}
          valueClassName="text-green-600"
        />

        <StatCard
          label="Inactive Brands"
          value={brands.length - activeBrands}
          valueClassName="text-red-500"
        />

        <StatCard label="Products Linked" value={productsLinked} />
      </div>

      <div className="overflow-hidden rounded-3xl border border-gray-100 bg-white">
        <div className="flex flex-col gap-4 border-b border-gray-100 p-5 md:flex-row md:items-center md:justify-between">
          <div className="relative w-full md:w-96">
            <FiSearch
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
              size={18}
            />

            <input
              type="text"
              placeholder="Search brands by name or code..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="h-12 w-full rounded-xl border border-gray-200 pl-12 pr-4 outline-none focus:border-primary-700"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            className="h-12 rounded-xl border border-gray-200 bg-white px-4 outline-none focus:border-primary-700"
          >
            <option value="ALL">All Status</option>
            <option value="ACTIVE">Active</option>
            <option value="INACTIVE">Inactive</option>
          </select>
        </div>

        {error && (
          <div className="m-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {actionError && (
          <div className="m-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {actionError}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase text-gray-500">
                  Brand
                </th>

                <th className="px-6 py-4 text-left text-xs font-semibold uppercase text-gray-500">
                  Code
                </th>

                <th className="px-6 py-4 text-left text-xs font-semibold uppercase text-gray-500">
                  Products
                </th>

                <th className="px-6 py-4 text-left text-xs font-semibold uppercase text-gray-500">
                  Status
                </th>

                <th className="px-6 py-4 text-right text-xs font-semibold uppercase text-gray-500">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
              {isLoading ? (
                <tr>
                  <td
                    colSpan="5"
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    Loading brands...
                  </td>
                </tr>
              ) : filteredBrands.length === 0 ? (
                <tr>
                  <td
                    colSpan="5"
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    No brands found.
                  </td>
                </tr>
              ) : (
                brandsPagination.pageItems.map((brand) => (
                  <tr
                    key={brand.id}
                    className="border-b border-gray-100 transition hover:bg-gray-50"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-4">
                        <BrandLogo brand={brand} />

                        <div>
                          <h4 className="font-semibold text-gray-900">
                            {brand.name}
                          </h4>

                          <p className="mt-1 text-xs text-gray-500">
                            {brand.description || "No description added"}
                          </p>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4 font-medium text-gray-700">
                      {brand.code}
                    </td>

                    <td className="px-6 py-4">
                      {brand._count?.products || brand.products?.length || 0}
                    </td>

                    <td className="px-6 py-4">
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-semibold ${
                          brand.isActive
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {brand.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => openEditModal(brand)}
                          className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 hover:bg-primary-50 hover:text-primary-700"
                          title={`Edit ${brand.name}`}
                        >
                          <FiEdit2 />
                        </button>

                        <button
                          type="button"
                          onClick={() => handleDelete(brand)}
                          className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-red-500 hover:bg-red-50"
                          title={`Delete ${brand.name}`}
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <ClientPagination pagination={brandsPagination} label="brands" />

        <div className="flex items-center justify-between p-5">
          <p className="text-sm text-gray-500">
            Showing {filteredBrands.length} brand
            {filteredBrands.length === 1 ? "" : "s"}
          </p>
        </div>
      </div>

      <BrandModal
        open={showModal}
        brand={selectedBrand}
        accessToken={accessToken}
        onClose={() => {
          setShowModal(false);
          setSelectedBrand(null);
        }}
        onSave={handleSave}
      />
    </div>
  );
}

function BrandLogo({ brand }) {
  const [imageFailed, setImageFailed] = useState(false);

  const imageUrl = brand.logoKey ? getImageUrl(brand.logoKey) : "";

  if (!imageUrl || imageFailed) {
    return (
      <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-primary-50 text-primary-700">
        <FiBox size={20} />
      </div>
    );
  }

  return (
    <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-gray-100 bg-white">
      <img
        src={imageUrl}
        alt={`${brand.name} logo`}
        onError={() => setImageFailed(true)}
        className="h-full w-full object-contain p-1.5"
      />
    </div>
  );
}

function StatCard({ label, value, valueClassName = "" }) {
  return (
    <div className="rounded-3xl border border-gray-100 bg-white p-6">
      <p className="text-sm text-gray-500">{label}</p>

      <h2 className={`mt-2 text-3xl font-bold ${valueClassName}`}>{value}</h2>
    </div>
  );
}
